import { memo, useCallback, useRef, useState } from 'react';
import {
  BREAKDOWN_PANEL_CATEGORIES,
  ELEMENT_CATEGORIES,
  REQUIREMENT_FACETS,
  REQUIREMENT_FACET_ORDER,
  readableInkOn,
  type ElementCategory,
  type RequirementFacet,
} from '@scoutaround/script-breakdown-shared';
import { ConversationEntry } from './conversations/ConversationEntry.js';
import { SceneConversationEntry } from './conversations/ConversationDrawer.js';
import { StatusPill } from './ui/StatusPill.js';

/**
 * Characters that belong to a word, for the purpose of not cutting one in half.
 *
 * Unicode letters and digits, plus the apostrophe and hyphen that live INSIDE
 * screenplay words — "DAVID'S", "walkie-talkie" — because stopping at them
 * would reintroduce the same bug one character further along.
 */
const WORD_CHARACTER = /[\p{L}\p{N}'\u2019-]/u;

/**
 * Grow a selection to whole words.
 *
 * A drag that stops one character short is invisible while making it and
 * permanent afterwards: "AN OLD WOODEN FENC" went onto the sheet as a prop, and
 * nothing downstream could tell it from a deliberate name. The producer is
 * pointing at a phrase, not at a character range, so the phrase is what gets
 * taken — this reads what they meant rather than what their mouse did.
 *
 * Whitespace-only selections come back empty, which the caller treats as no
 * selection at all.
 */
export function snapToWords(text: string, start: number, end: number): string {
  if (text === '') return '';

  let from = Math.max(0, Math.min(start, text.length));
  let to = Math.max(from, Math.min(end, text.length));

  // A drag across a space between two words is not a short selection of either
  // of them — it is not a selection of a phrase at all. Growing it would hand
  // back both neighbours, which is a stranger answer than none.
  if (!WORD_CHARACTER.test(text.slice(from, to))) return '';

  while (from > 0 && WORD_CHARACTER.test(text[from - 1] ?? '')) from -= 1;
  while (to < text.length && WORD_CHARACTER.test(text[to] ?? '')) to += 1;

  return text.slice(from, to).trim();
}

/** A run of screenplay text, optionally carrying what it was tagged as. */
export interface SceneSpan {
  readonly text: string;
  /** Tagged as a production element — rendered in that category's colour. */
  readonly category?: ElementCategory;
  /** Tagged as part of the location requirement — rendered in the facet's. */
  readonly facet?: RequirementFacet;
  /** The model proposed this and nobody has confirmed it yet. */
  readonly unconfirmed?: boolean;
}

export interface SceneLine {
  readonly kind: 'ACTION' | 'CHARACTER' | 'DIALOGUE' | 'PARENTHETICAL';
  readonly spans: readonly SceneSpan[];
}

export interface SceneCardScene {
  readonly id: string;
  readonly sceneNumber: string | null;
  /** Where it sits in the screenplay — what the number falls back to. */
  readonly ordinal?: number;
  readonly slugline: string | null;
  /** INT / EXT and the time of day, as the heading states them — the scene's identity at a glance. */
  readonly intExt?: string | null;
  readonly timeOfDay?: string | null;
  /** False when extraction resolved no location noun for the scene. */
  readonly locationRecognised: boolean;
  /** Why not — shown verbatim, because "not recognised" alone is not actionable. */
  readonly recognitionNote?: string | null;
  readonly lines: readonly SceneLine[];
  readonly elements: Readonly<Partial<Record<ElementCategory, readonly SceneElement[]>>>;
  /**
   * AI_DRAFT | IN_REVIEW | REVIEWED.
   *
   * Producer-visible on purpose: it is the difference between "nobody has
   * looked at this yet" and "somebody read it and it is right", and those are
   * the two states a first AD most needs to tell apart on a shooting schedule.
   */
  readonly reviewStatus?: 'AI_DRAFT' | 'IN_REVIEW' | 'REVIEWED';
  /**
   * The requirements extraction attached to this scene, with their status.
   * Absent on callers that render the sheet without the decision strip.
   */
  readonly requirements?: readonly SceneCardRequirement[];
}

/**
 * One thing the model read out of this scene, as the producer can act on it.
 *
 * IT CARRIES ITS ID because a list of strings can be displayed and nothing else:
 * every verb that changes what the model recognised is keyed by the mention, so
 * without this the card could show the reading and offer no way to disagree with
 * it — which left "the AI tagged the wrong thing" with no answer but ignoring it.
 */
export interface SceneElement {
  readonly id: string;
  readonly text: string;
  /**
   * AI_INFERENCE — the model read it, and it is removed by rejecting the
   * mention. HUMAN_CONFIRMED — somebody put it on the sheet, and it is removed
   * by taking it off this scene. Both are on the sheet and both come off it;
   * what differs is which record says so.
   */
  readonly provenance: 'AI_INFERENCE' | 'HUMAN_CONFIRMED';
  /** ACCEPT | REJECT | EDIT … or null while nobody has said anything. */
  readonly decision: string | null;
  /**
   * How many this scene needs, or null while nobody has counted. Null is a real
   * third state, not a default of one: everything downstream that adds these up
   * must be able to tell a counted one from an uncounted anything.
   */
  readonly quantity?: number | null;
  /**
   * A count the script itself states ("three police cars"), read by the AI. A HINT shown as
   * the count field's placeholder, never a count: the field stays empty until a person types
   * one (owner decision 2026-11-03).
   */
  readonly quantityHint?: number | null;
}

export type ElementDecision = 'ACCEPT' | 'REJECT' | 'RESTORE';

export type TagTarget =
  | { readonly kind: 'ELEMENT'; readonly category: ElementCategory }
  | { readonly kind: 'REQUIREMENT'; readonly facet: RequirementFacet };

/**
 * Where an element tag lands: this scene only, or every scene whose text names
 * it (owner review 2026-10-27, D2). A facet always belongs to this scene's
 * requirement, so it never carries one.
 */
export type TagScope = 'SCENE' | 'EVERY_SCENE';

/**
 * A location requirement this scene produced, as the producer decides on it.
 *
 * THE SAME ROW the Sourcing list shows — `status` is the requirement's own
 * status, and the decision buttons call the same approve and dismiss verbs.
 * Nothing here is a copy that would need reconciling later.
 */
export interface SceneCardRequirement {
  readonly id: string;
  readonly title: string;
  /** PROPOSED | APPROVED | DISMISSED. */
  readonly status: string;
  readonly confidenceBand?: string;
  /** The other scenes this requirement covers — a decision here reaches them. */
  readonly sharedWithSceneNumbers?: readonly string[];
  /**
   * What the location must be like — the values on the requirement, each with
   * the question it answers (`facet`) and how badly it is wanted (`kind`).
   * Absent where the caller does not hold the rows.
   */
  readonly constraints?: readonly SceneCardConstraint[];
  /** The creative brief the reading wrote for the place, when it wrote one. */
  readonly creativeDescription?: string | null;
}

export interface SceneCardConstraint {
  readonly id: string;
  /** MUST_HAVE | NICE_TO_HAVE | EXCLUDE | INFORMATIONAL. */
  readonly kind: string;
  /** One of `REQUIREMENT_FACET_ORDER`, or null for a value filed under none. */
  readonly facet: string | null;
  readonly label: string;
}

export type RequirementDecision = 'APPROVE' | 'DISMISS';

/**
 * One scene, as the producer works it: the screenplay, what the model read out
 * of it, and the affordance to disagree.
 *
 * WHY THE SCREENPLAY IS ON THE CARD. A breakdown sheet on its own asks the
 * producer to trust a list. Here the text and the list sit together and the
 * tagged words are highlighted IN the text, so "where did Picture vehicles get
 * a ferry from?" is answered by looking rather than by asking. That evidence
 * link is also what makes a correction worth keeping: deleting a tag records
 * that the model read THESE words and a professional judged them wrong, which
 * is a far stronger training signal than "a tag disappeared".
 *
 * EMPTY CATEGORIES STAY VISIBLE, and this is the thing a tidy implementation
 * always gets wrong. An empty Stunts cell is a STATEMENT that this scene has
 * none; hiding it would make that identical to nobody having looked yet, and
 * those are the two states a first AD most needs to tell apart.
 *
 * NOT RECOGNISED IS A STATE, NOT AN ERROR. A scene whose location noun did not
 * resolve — "the crossing", with no place named — is the normal, frequent case
 * in a real screenplay. It is shown as a fact with its reason and a way to fix
 * it, not as a failure, because the producer is the one who knows what "the
 * crossing" is.
 *
 * THE COLOURS ARE THE INDUSTRY'S. Element colours come from the shared contract
 * (`Production_Script_Breakdown_Colour_Tags.pdf`); a first AD reads a sheet by
 * colour before reading it by word. Requirement facets carry their own four,
 * for the same reason on the tagging surface.
 */
/**
 * The selection, restricted to ONE card's own requirements.
 *
 * The screen holds the whole selection in one array, because a location shared
 * by four scenes must show one state on all four and a merge spans scenes. That
 * array's identity changes on every tick, which without this would re-render
 * all 120-odd mounted cards — each of which re-renders the full scene text as
 * per-run highlight spans and a seventeen-category sheet — to change one
 * checkbox. A card only cares about the ids it actually draws.
 */
const selectionHere = (
  scene: SceneCardScene,
  selected: readonly string[] | undefined,
): string =>
  (scene.requirements ?? [])
    .filter((r) => selected?.includes(r.id) ?? false)
    .map((r) => r.id)
    .join(',');

function SceneCardBase({
  scene,
  projectId,
  onTag,
  onAddElement,
  onAddRequirement,
  onMarkReviewed,
  onUnmarkReviewed,
  onDecideElement,
  onRemoveElement,
  onSetQuantity,
  onDecideRequirement,
  onRestoreRequirement,
  selectedRequirementIds,
  onToggleRequirementSelected,
  decideRefusal = null,
  reviewRefusal = null,
  focus = 'ALL',
}: {
  scene: SceneCardScene;
  /**
   * WHICH WORKFLOW THE CARD IS SERVING (owner brief, 2026-10-29). Locations and
   * the detailed breakdown are independent workflows over the same scenes, so
   * each sees only its own half of the card: `LOCATIONS` the requirements, the
   * location facets and whether the location was recognised; `ELEMENTS` the
   * breakdown sheet, the element tagging and the scene's review gate. `ALL` —
   * the Script tab — keeps both.
   */
  focus?: 'ALL' | 'LOCATIONS' | 'ELEMENTS';
  /** When given, the scene, its requirements and its confirmed elements carry a
   *  "Discuss" control (ADR 0021). Omitted where conversations do not belong. */
  projectId?: string;
  /** A producer marked a selection as something — on this scene, or on every scene that names it. */
  onTag?: (sceneId: string, selection: string, target: TagTarget, scope: TagScope) => void;
  /**
   * Add an element by name to one category.
   *
   * The `+` on each of the seventeen cells called this and NOTHING PASSED IT —
   * seventeen dead buttons per card, on every card. The same shape of defect as
   * the text tagger and the facet buttons: a control that looks like an
   * affordance, answers a press with nothing, and says nothing about why.
   */
  onAddElement?: (sceneId: string, category: ElementCategory, name: string) => void;
  onAddRequirement?: (sceneId: string) => void;
  onMarkReviewed?: (sceneId: string) => void;
  /** Take the scene back out of reviewed — a review is revisable. */
  onUnmarkReviewed?: (sceneId: string) => void;
  /** Keep or drop something the model read out of this scene. */
  onDecideElement?: (mentionId: string, decision: ElementDecision) => void;
  /** Take a confirmed element off this scene's sheet. */
  onRemoveElement?: (sceneId: string, productionElementId: string) => void;
  /**
   * How many of it the scene needs. The element is identified by its own id —
   * a proposal's id is a mention's, and the container turns that into a line on
   * the sheet before it can carry a number.
   */
  onSetQuantity?: (sceneId: string, element: SceneElement, quantity: number | null) => void;
  /** "We need this location" / "We don't need this" on one of the scene's requirements. */
  onDecideRequirement?: (requirementId: string, decision: RequirementDecision) => void;
  /** Undo "We don't need this": a dismissed requirement back to proposed. */
  onRestoreRequirement?: (requirementId: string) => void;
  /**
   * WHICH REQUIREMENTS THE PRODUCER HAS PICKED OUT, for merging or splitting.
   *
   * Held by the screen, not by the card, for two reasons. A location shared by
   * four scenes is drawn on four cards and must show the same tick on all of
   * them. And a merge is precisely the operation that spans scenes — the two
   * requirements that are really one place are usually not on the same page —
   * so a per-card selection could never express the thing merging exists for.
   */
  selectedRequirementIds?: readonly string[];
  onToggleRequirementSelected?: (requirementId: string) => void;
  /**
   * Why this person cannot decide on a location, or correct the reading — in
   * words, resolved once by the screen rather than per card. Null means they
   * can. The hint is never the gate (`requirePermission` on the server is), and
   * an unknown answer is "they can", because hiding a control somebody is
   * entitled to leaves them no way to find out why.
   */
  decideRefusal?: string | null;
  reviewRefusal?: string | null;
}) {
  /**
   * THE TAGGER IS ANCHORED TO THE WORDS, not stacked above them.
   *
   * It used to render in the card's flow, so opening it pushed the screenplay
   * and everything under it down the page — the producer selected a phrase and
   * the phrase jumped out from under the cursor. `top`/`left` are measured
   * against the card, so the menu opens under the selection and the page does
   * not move at all.
   */
  const [selection, setSelection] = useState<{
    text: string;
    top: number;
    left: number;
  } | null>(null);
  /** This scene by default, every time the tagger opens: the wider write is a choice, never a carry-over. */
  const [scope, setScope] = useState<TagScope>('SCENE');
  const cardRef = useRef<HTMLElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // Closed where the screen is narrow, open where it is not — decided once,
  // at mount; the producer's own toggling wins after that.
  const [sheetOpen, setSheetOpen] = useState(() => !isNarrowScreen());
  const [scriptOpen, setScriptOpen] = useState(focus !== 'LOCATIONS');
  /**
   * A REMOVED ELEMENT LEAVES THE SHEET.
   *
   * It was struck through and left in place, on the reasoning that the model's
   * reading is worth seeing. That is true of the RECORD and false of the SHEET:
   * a producer who presses × on "AN OLD WOODEN FENCE" filed under Cast is
   * saying it does not belong there, and a sheet that keeps showing it has not
   * done what was asked. The decision is still stored, so the correction is
   * kept and the item can be brought back from behind one control.
   */
  const [showRemoved, setShowRemoved] = useState(false);
  /** The category whose `+` was pressed, and what is being typed into it. */
  const [addingIn, setAddingIn] = useState<ElementCategory | null>(null);
  const [newElement, setNewElement] = useState('');
  const allElements = Object.values(scene.elements).flatMap((items) => items ?? []);
  const removedCount = allElements.filter((item) => item.decision === 'REJECT').length;
  const taggedCount = allElements.length - removedCount;
  /**
   * The colours actually used in THIS scene's text.
   *
   * The legend beside the script listed the four REQUIREMENT facets while the
   * text was painted in the seventeen ELEMENT colours, so a red phrase had
   * nothing on screen saying red means Cast. Hovering gave a tooltip, which is
   * not orientation — it is an interrogation, one word at a time.
   *
   * Only the categories present, and in sheet order: all seventeen would be a
   * wall of colour that explains this scene no better than none.
   */
  /**
   * The requirement a facet would attach to.
   *
   * Exactly one, or none: a scene with two location requirements gives the
   * gesture no unambiguous target, and picking the first would file a demand
   * against whichever happened to be extracted first. Better to say so than to
   * guess on the producer's behalf.
   */
  const showLocations = focus !== 'ELEMENTS';
  const showElements = focus !== 'LOCATIONS';
  const requirementId =
    showLocations && scene.requirements !== undefined && scene.requirements.length === 1
      ? (scene.requirements[0]?.id ?? null)
      : null;

  const legend = BREAKDOWN_PANEL_CATEGORIES.filter((category) =>
    (scene.elements[category] ?? []).some((item) => item.decision !== 'REJECT'),
  );

  /**
   * The popover opens from a real text selection rather than a click target,
   * because the unit a producer wants to tag is a phrase they choose — "working
   * harbour", not whichever word happens to be a span already.
   */
  const captureSelection = useCallback(() => {
    const sel = window.getSelection();
    const body = bodyRef.current;

    // A stray click clears the selection and would otherwise flicker the
    // popover open and shut on every mouseup in the text.
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed || body === null) return;

    const range = sel.getRangeAt(0);
    if (!body.contains(range.commonAncestorContainer)) return;

    // Where the selection sits in the scene's text, measured rather than
    // guessed: the body is split into marked and unmarked spans, so a character
    // offset inside one node says nothing about the whole.
    const upToStart = range.cloneRange();
    upToStart.selectNodeContents(body);
    upToStart.setEnd(range.startContainer, range.startOffset);

    const full = body.textContent ?? '';
    const start = upToStart.toString().length;
    const snapped = snapToWords(full, start, start + range.toString().length);

    if (snapped === '') return;

    // Where to hang the menu: under the selection, measured against the card so
    // it stays put while the page scrolls. Clamped so a phrase near the right
    // edge does not open a menu off the side of it.
    // POSITION IS A NICETY; OPENING IS NOT. `Range.getBoundingClientRect` is not
    // implemented everywhere a component is rendered — jsdom has no layout at
    // all — and letting that throw took the whole menu with it, which is a
    // steep price for not knowing where to put it. Without a rect it opens at
    // the top of the card, which is where it used to live anyway.
    const frame = cardRef.current?.getBoundingClientRect();
    const rect =
      typeof range.getBoundingClientRect === 'function' ? range.getBoundingClientRect() : null;

    setScope('SCENE');
    setSelection({
      text: snapped,
      top: frame && rect ? rect.bottom - frame.top + 8 : 0,
      left: frame && rect ? Math.max(8, Math.min(rect.left - frame.left, frame.width - 340)) : 0,
    });
  }, []);

  const tag = (target: TagTarget) => {
    if (selection === null) return;
    onTag?.(scene.id, selection.text, target, target.kind === 'ELEMENT' ? scope : 'SCENE');
    setSelection(null);
  };

  // Whether the LOCATION noun resolved is a locations question; the detailed
  // breakdown does not flag a scene for it.
  const recognised = scene.locationRecognised || !showLocations;
  const sceneFacts = [scene.intExt, scene.timeOfDay].filter(
    (fact): fact is string => typeof fact === 'string' && fact.trim() !== '',
  );

  return (
    <article
      ref={cardRef}
      data-testid={`scene-card-${scene.id}`}
      data-recognised={recognised ? '1' : '0'}
      className="rq-card"
    >
      {/* ---------------------------------------------------------------- */}
      {/* Which scene: number, heading, and where and when it plays.         */}
      {/* ---------------------------------------------------------------- */}
      <header className="rq-card-head">
        <span data-testid="scene-card-number" className="rq-card-num">
          {scene.sceneNumber ?? (scene.ordinal === undefined ? '—' : String(scene.ordinal))}
        </span>

        <span className="rq-card-id">
          <span data-testid="scene-card-slugline" className="rq-card-slug">
            {scene.slugline ?? 'Untitled scene'}
          </span>
          {sceneFacts.length === 0 ? null : (
            <span className="rq-card-facts" data-testid="scene-card-facts">
              {sceneFacts.map((fact) => (
                <span key={fact} className="chip">
                  {fact}
                </span>
              ))}
            </span>
          )}
        </span>

        {recognised ? null : (
          <span className="rq-flag" data-kind="NOT_RECOGNISED" data-testid="scene-card-unrecognised">
            Not recognised
          </span>
        )}

        {projectId ? (
          <span className="rq-card-discuss">
            <SceneConversationEntry
              projectId={projectId}
              sceneId={scene.id}
              label={`Sc. ${scene.sceneNumber ?? (scene.ordinal === undefined ? '—' : String(scene.ordinal))}${scene.slugline ? ` — ${scene.slugline}` : ''}`}
            />
          </span>
        ) : null}
      </header>

      {/* ---------------------------------------------------------------- */}
      {/* The decision: is this a location the production needs, and what   */}
      {/* must it be like?                                                    */}
      {/* ---------------------------------------------------------------- */}
      {showLocations ? (
        <section className="rq-card-reqs" aria-label="Location requirements">
          <p className="colh rq-card-section">
            {`Location requirement${(scene.requirements ?? []).length === 1 ? '' : 's'}`}
          </p>

          {scene.requirements !== undefined && scene.requirements.length > 0 ? (
            <div data-testid="scene-card-requirements" className="rq-reqs">
              {scene.requirements.map((r) => (
                <div
                  key={r.id}
                  data-testid={`scene-card-requirement-${r.id}`}
                  data-status={r.status}
                  className="rq-req"
                >
                  <div className="rq-req-line">
                    {/* THE THING THAT MAKES MERGE AND SPLIT REACHABLE.
                        Both buttons read a selection, and this tick is what fills
                        it. Offered only on live rows: a dismissed location is one
                        the production said it does not need, and a superseded
                        one is already the losing half of an earlier merge. */}
                    {onToggleRequirementSelected !== undefined &&
                    r.status !== 'DISMISSED' &&
                    r.status !== 'SUPERSEDED' ? (
                      <input
                        type="checkbox"
                        className="rq-req-pick"
                        data-testid={`scene-card-select-requirement-${r.id}`}
                        // NAMED, not just "SELECT". A scene with three requirements
                        // draws three of these, and read out of context — in a
                        // screen reader's forms list, say — they would otherwise
                        // announce identically and say nothing about which location
                        // is about to be merged or split.
                        aria-label={`Select ${r.title} for merging or splitting`}
                        disabled={decideRefusal !== null}
                        title={decideRefusal ?? 'Select for merging or splitting'}
                        checked={selectedRequirementIds?.includes(r.id) ?? false}
                        onChange={() => onToggleRequirementSelected(r.id)}
                      />
                    ) : null}

                    <span className="rq-req-title">{r.title}</span>

                    <RequirementStatusTag status={r.status} />

                    {r.confidenceBand !== undefined && r.confidenceBand !== 'HIGH' ? (
                      <span className="rq-flag" data-kind="LOW_CONFIDENCE">
                        Low confidence
                      </span>
                    ) : null}

                    {projectId ? (
                      <ConversationEntry projectId={projectId} anchorType="REQUIREMENT" anchorId={r.id} label={r.title} compact />
                    ) : null}

                    {/* Approval and searching are SEPARATE decisions, so no search
                        starts here — that is the Explore step's own button.
                        KEEPING AND DISCARDING ARE BOTH HERE (owner, 2026-10-27):
                        the wish to remove one arises exactly here, reading the
                        scene. Dismissing is a soft state (ADR 0011) with Restore
                        beside it, so a discard made mid-detail costs one click. */}
                    <span className="rq-req-actions">
                      {r.status !== 'APPROVED' && r.status !== 'DISMISSED' ? (
                        <button
                          type="button"
                          className="ctl"
                          data-testid={`scene-card-approve-${r.id}`}
                          disabled={decideRefusal !== null}
                          title={decideRefusal ?? undefined}
                          onClick={() => onDecideRequirement?.(r.id, 'APPROVE')}
                        >
                          We need this location
                        </button>
                      ) : null}
                      {r.status !== 'DISMISSED' && r.status !== 'SUPERSEDED' ? (
                        <button
                          type="button"
                          className="ctl"
                          data-testid={`scene-card-dismiss-${r.id}`}
                          disabled={decideRefusal !== null}
                          title={decideRefusal ?? undefined}
                          onClick={() => onDecideRequirement?.(r.id, 'DISMISS')}
                        >
                          We don&rsquo;t need this
                        </button>
                      ) : null}
                      {r.status === 'DISMISSED' && onRestoreRequirement !== undefined ? (
                        <button
                          type="button"
                          className="ctl"
                          data-testid={`scene-card-restore-${r.id}`}
                          disabled={decideRefusal !== null}
                          title={decideRefusal ?? undefined}
                          onClick={() => onRestoreRequirement(r.id)}
                        >
                          Restore
                        </button>
                      ) : null}
                    </span>
                  </div>

                  {r.sharedWithSceneNumbers !== undefined && r.sharedWithSceneNumbers.length > 0 ? (
                    <p className="rq-req-shared" data-testid={`scene-card-shared-${r.id}`}>
                      {`Also scene${r.sharedWithSceneNumbers.length === 1 ? '' : 's'} ${r.sharedWithSceneNumbers.join(', ')} — a decision here applies there too`}
                    </p>
                  ) : null}

                  <RequirementValues requirement={r} />
                </div>
              ))}
            </div>
          ) : (
            <p className="mm rq-card-none" data-testid="scene-card-note">
              {scene.recognitionNote ?? 'No location requirement on this scene yet.'}
            </p>
          )}

          <button
            type="button"
            className="ctl rq-card-add"
            data-testid="scene-card-add-requirement"
            disabled={decideRefusal !== null}
            title={decideRefusal ?? undefined}
            onClick={() => onAddRequirement?.(scene.id)}
          >
            + Add a requirement to this scene
          </button>
        </section>
      ) : null}

      {/* ---------------------------------------------------------------- */}
      {/* The tagging popover — where a human correction begins.            */}
      {/* ---------------------------------------------------------------- */}
      {selection === null ? null : (
        <div
          data-testid="scene-card-tagger"
          role="dialog"
          aria-label={`Mark “${selection.text}” as`}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setSelection(null);
          }}
          style={{
            // Out of the flow: in it, opening the menu pushed the screenplay
            // down and the phrase jumped out from under the cursor.
            position: 'absolute',
            top: selection.top,
            left: selection.left,
            zIndex: 20,
            width: 'min(520px, calc(100% - 16px))',
            maxHeight: '60vh',
            overflowY: 'auto',
            // LIGHT, like every other panel in the workspace. It was a slab of
            // near-black, which made it the loudest thing on a screen whose
            // whole job is reading a screenplay, and put the seventeen category
            // colours on a ground none of them were chosen against.
            background: 'var(--color-bg)',
            color: 'var(--color-text)',
            border: '1px solid var(--color-neutral-300)',
            borderRadius: 2,
            boxShadow: '0 12px 32px rgba(35, 35, 35, 0.18)',
            padding: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
              padding: '10px 14px',
              borderBottom: '1px solid var(--color-divider)',
              background: 'var(--color-surface)',
            }}
          >
            <span
              style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              &ldquo;{selection.text}&rdquo;
            </span>
            <span className="mm" style={{ fontSize: 12 }}>
              mark as
            </span>
            <button
              type="button"
              data-testid="scene-card-tagger-close"
              aria-label="Cancel tagging"
              onClick={() => {
                setSelection(null);
              }}
              style={{
                marginLeft: 'auto',
                background: 'transparent',
                border: 0,
                color: 'inherit',
                fontSize: 18,
                lineHeight: 1,
                cursor: 'pointer',
              }}
            >
              ×
            </button>
          </div>

          {/* WHAT THE LOCATION MUST BE. One per row with its own note: there
              are only four, they are the less familiar half of the vocabulary,
              and "which tab do I want" is answered by the note rather than by
              guessing from a two-word label. */}
          {showLocations ? (
          <div style={{ padding: '12px 14px' }}>
            <p className="flbl" style={{ margin: '0 0 8px' }}>
              What the location must be
            </p>
            {requirementId === null ? (
              <p
                className="mm"
                data-testid="tag-facet-unavailable"
                style={{ fontSize: 12.5, margin: 0, lineHeight: 1.6 }}
              >
                {scene.requirements === undefined || scene.requirements.length === 0
                  ? 'This scene has no location requirement yet — add one from the scene list, and these become available.'
                  : 'This scene carries more than one location requirement, so there is no single one to add this to. Add it where the requirement is edited.'}
              </p>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                {REQUIREMENT_FACET_ORDER.map((facet) => {
                  const meta = REQUIREMENT_FACETS[facet];

                  return (
                    <button
                      key={facet}
                      type="button"
                      data-testid={`tag-facet-${facet}`}
                      onClick={() => {
                        tag({ kind: 'REQUIREMENT', facet });
                      }}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '10px 1fr',
                        gap: 10,
                        alignItems: 'start',
                        textAlign: 'left',
                        background: 'var(--color-bg)',
                        border: '1px solid var(--color-divider)',
                        borderLeft: `4px solid ${meta.colour}`,
                        padding: '8px 10px',
                        cursor: 'pointer',
                        font: 'inherit',
                        color: 'inherit',
                      }}
                    >
                      <i
                        aria-hidden="true"
                        style={{ width: 10, height: 10, background: meta.colour, marginTop: 4 }}
                      />
                      <span>
                        <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>
                          {meta.label}
                        </span>
                        <span className="mm" style={{ display: 'block', fontSize: 11.5, marginTop: 2 }}>
                          {meta.use}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          ) : null}

          {/* WHAT IS IN THE SCENE. Kept in the breakdown sheet's own order, not
              re-sorted by anything cleverer: a first AD reads these by colour in
              a fixed sequence, and a tagging surface that ordered them
              differently from the sheet below would be a second vocabulary. */}
          {showElements ? (
          <div
            style={{
              padding: '12px 14px',
              borderTop: showLocations ? '1px solid var(--color-divider)' : 0,
              background: 'var(--color-surface)',
            }}
          >
            <p className="flbl" style={{ margin: '0 0 8px' }}>
              What is in the scene
            </p>
            {/* WHERE IT LANDS, chosen before the category: the category press is
                the action, so the scope has to be set by the time it happens.
                "Every scene" asks again with the count and the scene list
                before anything is written. */}
            <div
              role="radiogroup"
              aria-label="Where to mark it"
              data-testid="tag-scope"
              style={{ display: 'flex', flexWrap: 'wrap', gap: 4, margin: '0 0 10px' }}
            >
              {(
                [
                  ['SCENE', 'This scene', 'tag-scope-scene'],
                  ['EVERY_SCENE', 'Every scene it appears in', 'tag-scope-every'],
                ] as const
              ).map(([value, label, testId]) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={scope === value}
                  className="ctl"
                  data-testid={testId}
                  data-on={scope === value ? '1' : '0'}
                  onClick={() => {
                    setScope(value);
                  }}
                  style={{
                    fontSize: 12.5,
                    fontWeight: scope === value ? 700 : 500,
                    borderColor: scope === value ? 'var(--color-accent)' : undefined,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: 4,
              }}
            >
              {BREAKDOWN_PANEL_CATEGORIES.map((category) => {
                const meta = ELEMENT_CATEGORIES[category];

                return (
                  <button
                    key={category}
                    type="button"
                    data-testid={`tag-element-${category}`}
                    title={meta.use}
                    onClick={() => {
                      tag({ kind: 'ELEMENT', category });
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      background: 'var(--color-bg)',
                      border: '1px solid var(--color-divider)',
                      padding: '7px 9px',
                      fontSize: 12.5,
                      textAlign: 'left',
                      cursor: 'pointer',
                      font: 'inherit',
                      color: 'inherit',
                    }}
                  >
                    <i
                      aria-hidden="true"
                      style={{ width: 9, height: 9, background: meta.colour, flex: '0 0 auto' }}
                    />
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
          ) : null}
        </div>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* The screenplay, with what the model read highlighted in place.    */}
      {/* A REFERENCE, folded on the Locations page: the requirement is the   */}
      {/* thing being reviewed there, and the text is one click away. On the  */}
      {/* detailed breakdown the text IS the working surface — words are      */}
      {/* tagged by selecting them — so it starts open.                       */}
      {/* ---------------------------------------------------------------- */}
      <details
        className="rq-card-script"
        data-testid="scene-card-script"
        open={scriptOpen}
        onToggle={(e) => {
          setScriptOpen(e.currentTarget.open);
        }}
      >
        <summary data-testid="scene-card-script-toggle">
          <span className="colh">Screenplay excerpt</span>
          <span className="mm rq-card-script-meta">
            Read-only · select words to tag them
          </span>
        </summary>
      {!showElements || legend.length === 0 ? null : (
        <div
          data-testid="scene-card-legend"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px 10px',
            padding: '10px 16px 0',
            alignItems: 'center',
          }}
        >
          <span className="mm" style={{ fontSize: 11, letterSpacing: '0.06em' }}>
            IN THIS SCENE
          </span>
          {legend.map((category) => {
            const meta = ELEMENT_CATEGORIES[category];

            return (
              <span
                key={category}
                data-testid={`scene-card-legend-${category}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  fontSize: 11.5,
                  background: meta.colour,
                  color: readableInkOn(meta.colour),
                  padding: '1px 7px',
                }}
              >
                {meta.label}
              </span>
            );
          })}
        </div>
      )}

      <div
        ref={bodyRef}
        data-testid="scene-card-text"
        onMouseUp={captureSelection}
        style={{
          padding: '20px 24px',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 13,
          lineHeight: 1.9,
        }}
      >
        {scene.lines.map((line, lineIndex) => (
          <p
            // Screenplay lines have no stable identity of their own; they are a
            // rendering of a text, and the text is the identity.
            key={`${scene.id}-line-${String(lineIndex)}`}
            style={{
              margin: 0,
              textAlign:
                line.kind === 'CHARACTER' || line.kind === 'DIALOGUE' || line.kind === 'PARENTHETICAL'
                  ? 'center'
                  : 'left',
            }}
          >
            {line.spans.map((span, spanIndex) => (
              <SceneTextSpan key={`${scene.id}-${String(lineIndex)}-${String(spanIndex)}`} span={span} />
            ))}
          </p>
        ))}
      </div>
      </details>

      {/* ---------------------------------------------------------------- */}
      {/* The breakdown sheet. All seventeen categories, always.            */}
      {/* ---------------------------------------------------------------- */}
      {showElements ? (
      <div className="rq-card-sheet">
        {/* ON A PHONE THE SHEET FOLDS. Seventeen categories per scene made a
            ninety-scene breakdown eighteen thousand pixels tall on a phone,
            most of it dashes. The sheet is still all there — the empty
            categories still say "none" once opened — it simply starts closed
            where the screen is narrow, and open where it is not. */}
        {showElements ? (
        <details
          data-testid="scene-card-breakdown-details"
          open={sheetOpen}
          onToggle={(e) => {
            setSheetOpen(e.currentTarget.open);
          }}
        >
          <summary
            data-testid="scene-card-breakdown-toggle"
            style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', cursor: 'pointer', listStyle: 'none' }}
          >
            <span className="colh" style={{ margin: 0 }}>
              Production breakdown
            </span>
            <span className="mm" style={{ fontSize: 12 }}>
              {`${String(taggedCount)} tagged · colour-tagged elements — empty categories stay visible`}
            </span>
          </summary>

        {/* THE WAY BACK. Removing is not destroying — the decision is stored,
            and an element taken off the sheet by mistake must not need a
            developer. Absent entirely while nothing has been removed, so a
            clean sheet carries no extra control. */}
        {removedCount === 0 ? null : (
          <p style={{ margin: '10px 0 0' }}>
            <button
              type="button"
              className="ctl"
              data-testid="scene-card-show-removed"
              aria-pressed={showRemoved}
              onClick={() => {
                setShowRemoved((on) => !on);
              }}
            >
              {showRemoved
                ? `Hide ${String(removedCount)} removed`
                : `${String(removedCount)} removed · show`}
            </button>
          </p>
        )}

        <div
          data-testid="scene-card-breakdown"
          style={{
            marginTop: 12,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
            gap: 1,
            background: 'var(--color-divider)',
            border: '1px solid var(--color-divider)',
          }}
        >
          {BREAKDOWN_PANEL_CATEGORIES.map((category) => {
            const meta = ELEMENT_CATEGORIES[category];
            const items = scene.elements[category] ?? [];
            const shownItems = showRemoved
              ? items
              : items.filter((item) => item.decision !== 'REJECT');

            return (
              <div
                key={category}
                data-testid={`scene-card-category-${category}`}
                data-empty={shownItems.length === 0 ? '1' : '0'}
                style={{ background: 'var(--color-bg)', padding: '10px 12px' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <i
                    aria-hidden="true"
                    style={{ width: 9, height: 9, background: meta.colour, flex: '0 0 auto' }}
                  />
                  <span
                    style={{
                      fontSize: 10,
                      letterSpacing: '0.07em',
                      color: 'var(--color-neutral-600)',
                      fontWeight: 600,
                    }}
                  >
                    {meta.label.toUpperCase()}
                  </span>
                  <button
                    type="button"
                    className="ctl"
                    data-testid={`scene-card-add-${category}`}
                    aria-label={`Add ${meta.label}`}
                    onClick={() => {
                      setAddingIn(addingIn === category ? null : category);
                      setNewElement('');
                    }}
                    style={{ marginLeft: 'auto', padding: '1px 7px', lineHeight: 1.4 }}
                  >
                    +
                  </button>
                </div>

                {addingIn === category ? (
                  <form
                    data-testid={`scene-card-add-form-${category}`}
                    style={{ display: 'flex', gap: 4, marginTop: 6 }}
                    onSubmit={(e) => {
                      e.preventDefault();
                      const name = newElement.trim();
                      if (name === '') return;

                      onAddElement?.(scene.id, category, name);
                      setAddingIn(null);
                      setNewElement('');
                    }}
                  >
                    <input
                      data-testid={`scene-card-add-input-${category}`}
                      autoFocus
                      value={newElement}
                      aria-label={`Add ${meta.label}`}
                      placeholder={meta.label}
                      onChange={(e) => {
                        setNewElement(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setAddingIn(null);
                          setNewElement('');
                        }
                      }}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        border: '1px solid var(--color-neutral-300)',
                        background: 'var(--color-bg)',
                        padding: '2px 6px',
                        fontSize: 12.5,
                      }}
                    />
                    <button
                      type="submit"
                      className="ctl"
                      data-testid={`scene-card-add-submit-${category}`}
                      disabled={newElement.trim() === ''}
                      style={{ padding: '1px 8px' }}
                    >
                      Add
                    </button>
                  </form>
                ) : null}

                {/* CHIPS, NOT A JOINED STRING. The producer's whole job on this
                    card is to agree or disagree with what the model read, and a
                    comma-separated line offers nothing to disagree with. A
                    rejected element is struck through rather than removed: the
                    reading stays visible, because "the model thought this and a
                    professional said no" is the correction worth keeping, and
                    the way back is one press. */}
                <div
                  style={{
                    margin: '6px 0 0',
                    fontSize: 13,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6,
                  }}
                >
                  {shownItems.length === 0 ? (
                    // An em dash, not a blank: the cell is answering "none",
                    // and a blank reads as "not filled in".
                    <span className="mm" aria-label="none">
                      —
                    </span>
                  ) : (
                    shownItems.map((item) => {
                      const rejected = item.decision === 'REJECT';
                      const byHand = item.provenance === 'HUMAN_CONFIRMED';

                      return (
                        <span
                          key={item.id}
                          data-testid={`scene-card-element-${item.id}`}
                          data-decision={item.decision ?? 'NONE'}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            border: '1px solid var(--color-divider)',
                            padding: '1px 4px 1px 7px',
                            opacity: rejected ? 0.55 : 1,
                          }}
                        >
                          <span style={{ textDecoration: rejected ? 'line-through' : 'none' }}>
                            {item.text}
                          </span>
                          {/* HOW MANY. A breakdown sheet is not a list of nouns:
                              "police car" and "six police cars" are different
                              days, different budgets and different call sheets,
                              and this is the first figure the schedule and the
                              quote are built from. Blank means nobody has
                              counted, which is not one — so the field is empty
                              rather than pre-filled, and clearing it says "I do
                              not know" again. */}
                          {rejected ? null : (
                            <input
                              type="number"
                              min={1}
                              max={9999}
                              inputMode="numeric"
                              data-testid={`scene-card-element-qty-${item.id}`}
                              aria-label={`How many ${item.text}`}
                              title={
                                item.quantityHint != null && item.quantity == null
                                  ? `The script says ${String(item.quantityHint)} — type it to count it`
                                  : 'How many this scene needs'
                              }
                              placeholder={item.quantityHint != null ? String(item.quantityHint) : '—'}
                              defaultValue={item.quantity ?? ''}
                              onBlur={(e) => {
                                const raw = e.target.value.trim();
                                const next = raw === '' ? null : Number(raw);
                                if (next !== null && (!Number.isInteger(next) || next < 1)) return;
                                if ((item.quantity ?? null) === next) return;

                                onSetQuantity?.(scene.id, item, next);
                              }}
                              style={{
                                width: 44,
                                border: '1px solid var(--color-divider)',
                                background: 'var(--color-bg)',
                                padding: '0 2px',
                                fontSize: 12,
                                textAlign: 'center',
                              }}
                            />
                          )}
                          {/* Only a CONFIRMED element is a production element with a
                              stable id to talk about; a model proposal is a
                              mention that may yet be rejected. */}
                          {projectId && byHand && !rejected ? (
                            <ConversationEntry projectId={projectId} anchorType="ELEMENT" anchorId={item.id} label={item.text} compact />
                          ) : null}
                          <button
                            type="button"
                            className="ctl"
                            data-testid={`scene-card-element-toggle-${item.id}`}
                            aria-label={
                              rejected
                                ? `Put “${item.text}” back`
                                : `Take “${item.text}” off this scene`
                            }
                            title={
                              rejected
                                ? 'Put it back'
                                : byHand
                                  ? 'Take it off this scene'
                                  : 'The model read this and it is wrong'
                            }
                            onClick={() => {
                              // Both come off the sheet; which record says so
                              // depends on which record put it there.
                              if (byHand) onRemoveElement?.(scene.id, item.id);
                              else onDecideElement?.(item.id, rejected ? 'RESTORE' : 'REJECT');
                            }}
                            style={{ padding: '0 5px', lineHeight: 1.4 }}
                          >
                            {rejected ? '↺' : '×'}
                          </button>
                        </span>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </details>
        ) : null}

        <div className="rq-card-gate">
          {/* THE GATE, made visible and reversible.
              Until a scene is reviewed, an untouched AI item counts as nothing
              — the producer may never have opened that category — so nothing on
              this card is an accepted prediction yet. After it, an untouched
              EMPTY category is a confirmed negative: "there really are no
              animals in this scene", which is a fact worth having. */}
          {showElements ? (
          <span
            data-testid="scene-card-review"
            data-status={scene.reviewStatus ?? 'AI_DRAFT'}
            className="rq-card-review"
          >
            {scene.reviewStatus === 'REVIEWED' ? (
              <>
                <StatusPill state="CONFIRMED" label="Reviewed" />
                {/* A REVIEW IS REVISABLE. It was a one-way tick: a producer who
                    marked a scene and then noticed a missed location had no way
                    back from this card at all. Reviewing is a bookmark over a
                    human pass, not an approval later work depends on. */}
                <button
                  type="button"
                  className="ctl"
                  data-testid="scene-card-unmark-reviewed"
                  disabled={reviewRefusal !== null}
                  title={reviewRefusal ?? undefined}
                  onClick={() => onUnmarkReviewed?.(scene.id)}
                >
                  Not reviewed after all
                </button>
              </>
            ) : (
              <>
                {scene.reviewStatus === 'IN_REVIEW' ? (
                  // Not the same as never looked at: something changed after a
                  // review, and saying so is what stops a stale confirmation
                  // being read as a current one.
                  <span className="mm" style={{ fontSize: 12 }}>
                    changed since it was reviewed
                  </span>
                ) : null}
                <button
                  type="button"
                  className="ctl"
                  data-testid="scene-card-mark-reviewed"
                  disabled={reviewRefusal !== null}
                  title={reviewRefusal ?? undefined}
                  onClick={() => onMarkReviewed?.(scene.id)}
                >
                  Mark reviewed
                </button>
              </>
            )}
          </span>
          ) : null}

        </div>
      </div>
      ) : null}
    </article>
  );
}

/**
 * The requirement's status, in the reader's words rather than the enum's, in
 * the workspace's one status vocabulary: awaiting a decision is "ready for
 * review", approved is confirmed, not needed is set aside.
 */
function RequirementStatusTag({ status }: { status: string }) {
  if (status === 'APPROVED') return <StatusPill state="CONFIRMED" label="Approved" size="sm" />;
  if (status === 'DISMISSED') return <StatusPill state="EXCLUDED" label="Not needed" size="sm" />;
  if (status === 'SUPERSEDED') return <StatusPill state="EXCLUDED" label="Merged" size="sm" />;

  return <StatusPill state="READY_FOR_REVIEW" label="Awaiting your decision" size="sm" />;
}

/** How a value is wanted, as the matching uses it (see `ValueLegend`). */
const KIND_LABEL: Readonly<Record<string, string>> = {
  MUST_HAVE: 'Must',
  NICE_TO_HAVE: 'Nice',
  EXCLUDE: 'Not',
};

/**
 * WHAT THE LOCATION MUST BE LIKE, grouped by the question each value answers.
 *
 * The groups are the facets the tagging surface already uses — the place
 * itself, how it must look, what it must physically have, what must be true to
 * shoot there — read from the contract in its order, so a value tagged from the
 * text lands under the heading the tagger showed. Each chip says how badly it is
 * wanted, because "Must" removes candidates and "Nice" only ranks them. Nothing
 * is drawn for a group with no value: an empty facet here is "nobody said",
 * not "none".
 */
function RequirementValues({ requirement }: { requirement: SceneCardRequirement }) {
  const constraints = requirement.constraints ?? [];
  const brief = requirement.creativeDescription?.trim() ?? '';
  if (constraints.length === 0 && brief === '') return null;

  const groups = [
    ...REQUIREMENT_FACET_ORDER.map((facet) => ({
      key: facet,
      label: REQUIREMENT_FACETS[facet].label,
      values: constraints.filter((c) => c.facet === facet),
    })),
    {
      key: 'OTHER',
      label: 'Other values',
      values: constraints.filter(
        (c) => c.facet === null || !(REQUIREMENT_FACET_ORDER as readonly string[]).includes(c.facet),
      ),
    },
  ].filter((group) => group.values.length > 0);

  return (
    <div className="rq-values" data-testid={`scene-card-values-${requirement.id}`}>
      {brief === '' ? null : (
        <p className="rq-values-brief" data-testid={`scene-card-brief-${requirement.id}`}>
          {brief}
        </p>
      )}
      {groups.map((group) => (
        <div key={group.key} className="rq-values-group" data-testid={`scene-card-facet-${requirement.id}-${group.key}`}>
          <span className="rq-values-label">{group.label}</span>
          <span className="rq-values-chips">
            {group.values.map((c) => (
              <span key={c.id} className="rq-value" data-kind={c.kind}>
                {KIND_LABEL[c.kind] === undefined ? null : <b>{KIND_LABEL[c.kind]}</b>}
                {c.label}
              </span>
            ))}
          </span>
        </div>
      ))}
    </div>
  );
}

/** A phone, by the same breakpoint the stylesheet uses for stacked layouts. */
function isNarrowScreen(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(max-width: 760px)').matches
    : false;
}

/**
 * One run of screenplay text.
 *
 * A tagged run is marked IN the text rather than merely listed below it, so the
 * producer can see what the model read. An unconfirmed tag is outlined rather
 * than filled: a proposal and a decision should not look the same, and the
 * difference has to survive a black-and-white print of the sheet.
 */
function SceneTextSpan({ span }: { span: SceneSpan }) {
  if (span.category === undefined && span.facet === undefined) {
    return <>{span.text}</>;
  }

  const colour =
    span.category !== undefined
      ? ELEMENT_CATEGORIES[span.category].colour
      : REQUIREMENT_FACETS[span.facet as RequirementFacet].colour;

  const label =
    span.category !== undefined
      ? ELEMENT_CATEGORIES[span.category].label
      : REQUIREMENT_FACETS[span.facet as RequirementFacet].label;

  return (
    <mark
      data-testid="scene-card-mark"
      data-unconfirmed={span.unconfirmed ? '1' : '0'}
      title={span.unconfirmed ? `${label} — proposed, not confirmed` : label}
      style={
        span.unconfirmed
          ? {
              background: 'transparent',
              color: 'inherit',
              boxShadow: `inset 0 0 0 1.5px ${colour}`,
              padding: '1px 5px',
            }
          : // The ink follows the fill. `color: 'inherit'` put the body's
            // near-black on every swatch, and ten of the twenty-four are dark
            // enough that the marked words fell below 4.5:1 — Equipment reached
            // 2.0. The colour is what tells a first AD what kind of element it
            // is, so the colour stays and the ink moves.
            { background: colour, color: readableInkOn(colour), padding: '1px 5px' }
      }
    >
      {span.text}
    </mark>
  );
}


/**
 * MEMOISED, and deliberately not with the default comparison.
 *
 * Every other prop is compared by identity, which is why the screen hoists its
 * handlers instead of building closures inside the render loop — a fresh
 * closure per card per render would defeat any memo. `selectedRequirementIds`
 * is the exception: it is one array shared by every card, so it changes on
 * every tick and would fail an identity check on all of them. It is compared by
 * what it means for THIS card instead, so ticking a requirement re-renders the
 * cards that draw it and nothing else.
 */
export const SceneCard = memo(SceneCardBase, (prev, next) => {
  const keys = Object.keys(next) as (keyof Parameters<typeof SceneCardBase>[0])[];
  if (Object.keys(prev).length !== keys.length) return false;

  for (const key of keys) {
    if (key === 'selectedRequirementIds') continue;
    if (prev[key] !== next[key]) return false;
  }

  return (
    selectionHere(next.scene, prev.selectedRequirementIds) ===
    selectionHere(next.scene, next.selectedRequirementIds)
  );
});
